import clsx from 'clsx'

export interface CruiseDetailsProps {
  /**
   * Cruise session details (cruise line, ship, cabin, reservation #, dates).
   */
  cruise?: CruiseSession | null
  /**
   * The cruise port for this leg-end. Outbound legs board at the embarkation
   * port; return legs depart the debarkation port. Resolved by the caller since
   * only it knows which end of the leg is the cruise. Falls back to whichever
   * port the session carries.
   */
  portName?: string | null
  /**
   * Optional additional class name for the list container.
   */
  className?: string
}

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

/**
 * Normalises a cruise date to "May 17, 2027".
 *
 * The same block is fed by two APIs that format dates differently: the GraphQL
 * order query already returns "May 17, 2027", while `/v5/track/{tracking_id}`
 * (which backs the shipment details modal) returns "05/17/2027". Without this
 * the modal and the page disagree on the same screen.
 *
 * Deliberately string-only — parsing "05/17/2027" into a Date and reformatting
 * risks a timezone shift moving the date by a day. Anything that doesn't match
 * a known shape is passed through untouched rather than blanked, so an
 * unexpected format degrades to "shows the raw value" instead of "shows
 * nothing".
 */
const formatCruiseDate = (value?: string | null): string | null | undefined => {
  if (!value) return value

  const match =
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value) ?? // MM/DD/YYYY — /v5/track
    null

  if (match) {
    const [, month, day, year] = match
    const name = MONTHS[Number(month) - 1]
    return name ? `${name} ${Number(day)}, ${year}` : value
  }

  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(value) // YYYY-MM-DD
  if (iso) {
    const [, year, month, day] = iso
    const name = MONTHS[Number(month) - 1]
    return name ? `${name} ${Number(day)}, ${year}` : value
  }

  return value
}

/**
 * Renders the cruise details block for a shipment leg. Shows the required
 * fields — Cruise Line, Ship Name, Port Name, Cabin, Reservation #, Embarkation
 * Date, Debarkation Date — omitting any the payload doesn't carry. Returns null
 * when there is nothing to show, so callers can render it unconditionally.
 */
export default function CruiseDetails({
  cruise,
  portName,
  className,
}: CruiseDetailsProps) {
  const port =
    portName || cruise?.embarkation_port || cruise?.disembarkation_port

  const rows: Array<{ label: string; value?: string | null }> = [
    { label: 'Cruise Line', value: cruise?.cruise_line },
    { label: 'Ship Name', value: cruise?.ship_name },
    { label: 'Port Name', value: port },
    { label: 'Cabin', value: cruise?.cabin },
    { label: 'Reservation #', value: cruise?.reservation_number },
    {
      label: 'Embarkation Date',
      value: formatCruiseDate(cruise?.embarkation_date),
    },
    {
      label: 'Debarkation Date',
      value: formatCruiseDate(cruise?.disembarkation_date),
    },
  ].filter((row) => Boolean(row.value))

  if (rows.length === 0) return null

  return (
    <dl
      className={clsx(
        'flex flex-col gap-1 border-t border-neutral-400 pt-2 type-body-8',
        className,
      )}
    >
      {rows.map(({ label, value }) => (
        <div key={label} className="flex gap-1">
          <dt className="font-semibold text-neutral-700">{label}:</dt>
          <dd className="text-neutral-700">{value}</dd>
        </div>
      ))}
    </dl>
  )
}
